import React, { useState, useEffect, useCallback } from 'react';
import { Tab } from '@headlessui/react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../services/apiClient';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Bars3Icon } from '@heroicons/react/24/outline';

const ROLE_LABELS = {
  dentist: 'Dentiste',
  generalist: 'Généraliste',
  hygienist: 'Hygiéniste',
  insurance: 'Assurance',
  orthodentist: 'Orthodontiste',
  parodontologist: 'Parodontologiste',
  pediatrician: 'Pédiatre',
  vet: 'Vétérinaire',
};

export default function KeywordsAdmin() {
  const [roles, setRoles] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [existingItemKeyword, setExistingItemKeyword] = useState('');
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState('');
  const [editingKeywordItem, setEditingKeywordItem] = useState(null); // { itemId: number, keywordIndex: number }
  const [editingKeywordValue, setEditingKeywordValue] = useState('');

  // Fetch roles (sub_roles) on mount
  useEffect(() => {
    async function fetchRoles() {
      try {
        const data = await apiClient.get('/api/admin/roles/sub-roles');
        setRoles(data);
        if (data.length > 0) {
          setSelectedRole(data[0]);
          setSelectedIndex(0);
        }
      } catch (err) {
        setError("Erreur lors du chargement des rôles.");
      }
    }
    fetchRoles();
  }, []);

  const fetchKeywords = useCallback(async (role) => {
    try {
      setLoading(true);
      const data = await apiClient.get(`/api/admin/keywords-admin/${role}`);
      setKeywords(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching keywords:', err);
      setError("Erreur lors du chargement des mots clés.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRole) fetchKeywords(selectedRole);
  }, [selectedRole, fetchKeywords]);

  // When adding a new keyword, set its rank to max + 1
  const handleAddKeyword = async (description = '') => {
    if (!description && !editingDescription) return;
    try {
      setSaving(true);
      setError(null);
      // Find the next available rank
      const nextRank = keywords.length > 0 ? Math.max(...keywords.map(k => k.rank ?? 0)) + 1 : 0;
      const newItem = await apiClient.post('/api/admin/keywords-admin', {
        role: selectedRole,
        description: description || editingDescription,
        keywords: newKeyword,
        rank: nextRank
      });
      setSuccess(true);
      setNewKeyword('');
      setEditingDescription('');
      await fetchKeywords(selectedRole);
      setTimeout(() => {
        const input = document.getElementById(`keyword-input-${newItem.id}`);
        if (input) {
          setEditingId(newItem.id);
          input.focus();
        }
      }, 100);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error adding keyword:', err);
      setError("Erreur lors de l'ajout du mot clé.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateKeywords = async (id, updatedKeywords) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.patch(`/api/admin/keywords-admin/${id}`, {
        keywords: updatedKeywords
      });

      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error updating keywords:', err);
      setError("Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDescription = async (id, updatedDescription) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.patch(`/api/admin/keywords-admin/${id}`, {
        description: updatedDescription
      });

      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error updating description:', err);
      setError("Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  const handleDescriptionDoubleClick = (item) => {
    setEditingDescriptionId(item.id);
    setEditingDescriptionValue(item.description);
  };

  const handleDescriptionBlur = async (item) => {
    if (editingDescriptionValue.trim() && editingDescriptionValue !== item.description) {
      await handleUpdateDescription(item.id, editingDescriptionValue.trim());
    }
    setEditingDescriptionId(null);
    setEditingDescriptionValue('');
  };

  const handleDescriptionKeyDown = (e, item) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDescriptionBlur(item);
    } else if (e.key === 'Escape') {
      setEditingDescriptionId(null);
      setEditingDescriptionValue('');
    }
  };

  const handleKeywordDoubleClick = (item, keywordIndex, currentKeyword) => {
    setEditingKeywordItem({ itemId: item.id, keywordIndex });
    setEditingKeywordValue(currentKeyword.trim());
  };

  const handleKeywordBlur = async (item, keywordIndex) => {
    if (!editingKeywordItem || editingKeywordItem.itemId !== item.id || editingKeywordItem.keywordIndex !== keywordIndex) {
      return;
    }

    const keywordsArray = item.keywords.split(',').map(k => k.trim());
    const oldKeyword = keywordsArray[keywordIndex];
    const newKeyword = editingKeywordValue.trim();

    if (newKeyword && newKeyword !== oldKeyword) {
      // Replace the keyword at the specified index
      keywordsArray[keywordIndex] = newKeyword;
      const updatedKeywords = keywordsArray.join(', ');
      await handleUpdateKeywords(item.id, updatedKeywords);
    }

    setEditingKeywordItem(null);
    setEditingKeywordValue('');
  };

  const handleKeywordKeyDown = (e, item, keywordIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleKeywordBlur(item, keywordIndex);
    } else if (e.key === 'Escape') {
      setEditingKeywordItem(null);
      setEditingKeywordValue('');
    }
  };

  const handleDeleteKeyword = async (id) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.delete(`/api/admin/keywords-admin/${id}`);
      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error deleting keyword:', err);
      setError("Erreur lors de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeywordTag = (item, keywordToRemove) => {
    const updatedKeywords = item.keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k !== keywordToRemove)
      .join(', ');
    
    handleUpdateKeywords(item.id, updatedKeywords);
  };

  const handleAddKeywordTag = async (item) => {
    if (!existingItemKeyword.trim()) return;
    
    const existingKeywords = item.keywords ? item.keywords.split(',').map(k => k.trim()) : [];
    const updatedKeywords = [...existingKeywords, existingItemKeyword.trim()].join(', ');
    
    try {
      await handleUpdateKeywords(item.id, updatedKeywords);
      const currentId = item.id;
      setExistingItemKeyword('');
      
      // Keep the editingId set to the current item
      setEditingId(currentId);
      
      // Focus on the input field after state updates
      setTimeout(() => {
        const input = document.getElementById(`keyword-input-${currentId}`);
        if (input) {
          input.focus();
        }
      }, 100);
    } catch (err) {
      console.error('Error adding keyword tag:', err);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  // Handle drag and drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(keywords);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    // Update rank in state
    const updated = reordered.map((item, idx) => ({ ...item, rank: idx }));
    setKeywords(updated);
    // Persist new order to backend
    for (const item of updated) {
      await apiClient.patch(`/api/admin/keywords-admin/${item.id}/rank`, { rank: item.rank });
    }
    // Optionally, refetch to ensure sync
    fetchKeywords(selectedRole);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg" data-section="keywords">
      <div className="px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          Paramétrage des mots-clés
        </h2>
      </div>

      <div className="px-6 py-4">
        <Tab.Group 
          selectedIndex={selectedIndex} 
          onChange={(index) => {
            setSelectedIndex(index);
            const role = roles[index];
            setSelectedRole(role);
            fetchKeywords(role);
            // Reset form state when changing tabs
            setNewKeyword('');
            setExistingItemKeyword('');
            setEditingDescription('');
            setEditingId(null);
            setEditingDescriptionId(null);
            setEditingDescriptionValue('');
            setEditingKeywordItem(null);
            setEditingKeywordValue('');
          }}
        >
          <Tab.List className="flex space-x-1 rounded-xl bg-[#E0EAFE] p-1">
            {roles.map((role) => (
              <Tab
                key={role}
                className={({ selected }) => `
                  w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors
                  ${selected
                    ? 'bg-white text-gray-700 shadow'
                    : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-700'}
                `}
              >
                {ROLE_LABELS[role.toLowerCase()] || role}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            {roles.map((role) => (
              <Tab.Panel key={role} className="space-y-4">
                {/* Add new keyword section */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Description"
                        value={editingDescription}
                        onChange={(e) => setEditingDescription(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, () => {
                          if (editingDescription && newKeyword) {
                            handleAddKeyword();
                          }
                        })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm mb-2"
                      />
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          placeholder="Mot clé"
                          value={newKeyword}
                          onChange={(e) => setNewKeyword(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, () => {
                            if (editingDescription && newKeyword) {
                              handleAddKeyword();
                            }
                          })}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                        <button
                          onClick={() => handleAddKeyword()}
                          disabled={saving || !editingDescription || !newKeyword}
                          className={`inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium text-gray-700 rounded-md bg-[rgb(141,212,209)] ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(141,212,209)] disabled:bg-[rgb(141,212,209)] disabled:bg-opacity-50 disabled:text-gray-500`}
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Ajouter
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Keywords list */}
                <div className="bg-white rounded-lg shadow-sm">
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="keywords-list">
                      {(provided) => (
                        <ul className="divide-y divide-gray-200"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                          {keywords
                            .slice()
                            .sort((a, b) => a.rank - b.rank)
                            .map((item, index) => (
                            <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                              {(provided, snapshot) => (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`p-4 bg-white ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 flex items-center gap-2">
                                      <span {...provided.dragHandleProps} className="cursor-grab flex items-center mr-2">
                                        <Bars3Icon className="h-5 w-5 text-gray-400" />
                                      </span>
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                                          <span className="inline-block w-6 text-xs text-gray-400">#{item.rank + 1}</span>
                                          {editingDescriptionId === item.id ? (
                                            <input
                                              type="text"
                                              value={editingDescriptionValue}
                                              onChange={(e) => setEditingDescriptionValue(e.target.value)}
                                              onBlur={() => handleDescriptionBlur(item)}
                                              onKeyDown={(e) => handleDescriptionKeyDown(e, item)}
                                              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                              autoFocus
                                            />
                                          ) : (
                                            <span
                                              onDoubleClick={() => handleDescriptionDoubleClick(item)}
                                              className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                              title="Double-cliquez pour modifier"
                                            >
                                              {item.description}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {item.keywords.split(',').map((keyword, idx) => (
                                            editingKeywordItem?.itemId === item.id && editingKeywordItem?.keywordIndex === idx ? (
                                              <input
                                                key={idx}
                                                type="text"
                                                value={editingKeywordValue}
                                                onChange={(e) => setEditingKeywordValue(e.target.value)}
                                                onBlur={() => handleKeywordBlur(item, idx)}
                                                onKeyDown={(e) => handleKeywordKeyDown(e, item, idx)}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border-2 border-blue-500"
                                                autoFocus
                                                style={{ minWidth: '80px' }}
                                              />
                                            ) : (
                                              <span
                                                key={idx}
                                                onDoubleClick={() => handleKeywordDoubleClick(item, idx, keyword)}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                                                title="Double-cliquez pour modifier"
                                              >
                                                {keyword.trim()}
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveKeywordTag(item, keyword.trim());
                                                  }}
                                                  className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-300 hover:text-blue-600 focus:outline-none"
                                                >
                                                  <XMarkIcon className="h-3 w-3" />
                                                </button>
                                              </span>
                                            )
                                          ))}
                                          {/* Add keyword to existing description */}
                                          {(!editingKeywordItem || editingKeywordItem.itemId !== item.id) && (
                                            <div className="inline-flex items-center">
                                              <input
                                                id={`keyword-input-${item.id}`}
                                                type="text"
                                                placeholder="Ajouter plus"
                                                value={editingId === item.id ? existingItemKeyword : ''}
                                                onChange={(e) => {
                                                  setEditingId(item.id);
                                                  setExistingItemKeyword(e.target.value);
                                                }}
                                                onFocus={() => setEditingId(item.id)}
                                                onKeyDown={(e) => handleKeyDown(e, () => {
                                                  if (existingItemKeyword) {
                                                    handleAddKeywordTag(item);
                                                  }
                                                })}
                                                className="w-24 text-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                              />
                                              <button
                                                onClick={() => handleAddKeywordTag(item)}
                                                disabled={saving || !existingItemKeyword}
                                                className="ml-1 p-1 text-gray-400 hover:text-gray-500"
                                              >
                                                <PlusIcon className="h-4 w-4" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleDeleteKeyword(item.id)}
                                      className="ml-4 text-gray-400 hover:text-gray-500"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>

        {error && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            Mot clé ajouté avec succès !
          </div>
        )}
      </div>
    </div>
  );
} 